import { useQuery, useMutation, QueryResult } from '@apollo/client';
import { Heading, Stack } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import { useParams } from 'react-router';

import { companyIntegrationsListQuery, companyWebhookSubscriptionsQuery } from '../../company/graphql';
import {
  CompanyWebhookSubscriptionInfo,
  CompanyWebhookSubscriptionQuery,
  CompanyWebhookSubscriptionQueryVariables,
  CreateWebhookSubscriptionInput,
  CreateWebhookSubscriptionMutation,
  CreateWebhookSubscriptionMutationVariables,
  DeleteWebhookSubscriptionInput,
  DeleteWebhookSubscriptionMutation,
  DeleteWebhookSubscriptionMutationVariables,
  UpdateWebhookSubscriptionInput,
  UpdateWebhookSubscriptionMutation,
  UpdateWebhookSubscriptionMutationVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { WebhookSubscriptionForm } from '../components/webhook_subscription/webhook_subscription_form';
import {
  companyWebhookSubscriptionQuery,
  createWebhookSubscriptionMutation,
  deleteWebhookSubscriptionMutation,
  updateWebhookSubscriptionMutation,
} from '../graphql';

import styles from './styles.module.css';

class WebhookSubscriptionLoadingContainer extends Loader<
  CompanyWebhookSubscriptionQuery,
  EditModeLoaderProps,
  CompanyWebhookSubscriptionQueryVariables
> {}

interface EditModeLoaderProps {
  company_id: string;
  subscription: CompanyWebhookSubscriptionInfo | null;
  onSave: (input: UpdateWebhookSubscriptionInput) => Promise<void>;
  onDelete: (input: DeleteWebhookSubscriptionInput) => Promise<void>;
}

function CreateModeContainer({ company_id }: { company_id: string }) {
  const [createWebhookSubscription] = useMutation<
    CreateWebhookSubscriptionMutation,
    CreateWebhookSubscriptionMutationVariables
  >(createWebhookSubscriptionMutation);

  return (
    <WebhookSubscriptionForm
      mode="create"
      company_id={company_id}
      onCreate={async (input: CreateWebhookSubscriptionInput) => {
        const result = await createWebhookSubscription({ variables: { input } });
        const subscriptionId = result?.data?.createWebhookSubscription?.subscriptionId;
        if (isNil(subscriptionId)) {
          throw new Error('Failed to create webhook subscription');
        }
        return subscriptionId;
      }}
    />
  );
}

function EditModeContainer({
  company_id,
  webhook_subscription_id,
}: {
  company_id: string;
  webhook_subscription_id: string;
}) {
  const result = useQuery<CompanyWebhookSubscriptionQuery, CompanyWebhookSubscriptionQueryVariables>(
    companyWebhookSubscriptionQuery,
    { variables: { company_id, subscription_id: webhook_subscription_id } },
  );

  return (
    <EditModeDataLoader result={result} company_id={company_id} webhook_subscription_id={webhook_subscription_id} />
  );
}

function EditModeDataLoader({
  result,
  company_id,
  webhook_subscription_id,
}: {
  result: QueryResult<CompanyWebhookSubscriptionQuery, CompanyWebhookSubscriptionQueryVariables>;
  company_id: string;
  webhook_subscription_id: string;
}) {
  const [deleteWebhookSubscription] = useMutation<
    DeleteWebhookSubscriptionMutation,
    DeleteWebhookSubscriptionMutationVariables
  >(deleteWebhookSubscriptionMutation);

  const [updateWebhookSubscription] = useMutation<
    UpdateWebhookSubscriptionMutation,
    UpdateWebhookSubscriptionMutationVariables
  >(updateWebhookSubscriptionMutation);

  return (
    <WebhookSubscriptionLoadingContainer
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result={result as any}
      errorPlaceholder={() => (
        <Stack direction="column" className={styles.webhookSubscriptionContainer}>
          <Heading>Webhook Subscription</Heading>
          <p>Webhook subscription not found. It may have been deleted.</p>
        </Stack>
      )}
      mapData={({ data }) => ({
        company_id,
        subscription: data.company?.companyWebhookSubscription ?? null,
        onSave: async (input: UpdateWebhookSubscriptionInput) => {
          await updateWebhookSubscription({
            variables: { input },
            refetchQueries: [
              {
                query: companyWebhookSubscriptionQuery,
                variables: { company_id, subscription_id: webhook_subscription_id },
              },
            ],
          });
        },
        onDelete: async (input: DeleteWebhookSubscriptionInput) => {
          await deleteWebhookSubscription({
            variables: { input },
            refetchQueries: [
              {
                query: companyWebhookSubscriptionsQuery,
                variables: { company_id },
              },
              {
                query: companyIntegrationsListQuery,
                variables: { company_id },
              },
            ],
          });
        },
      })}>
      {({ company_id, subscription, onSave, onDelete }) => {
        if (isNil(subscription)) {
          return (
            <Stack direction="column" className={styles.webhookSubscriptionContainer}>
              <Heading>Webhook Subscription</Heading>
              <p>Webhook subscription not found.</p>
            </Stack>
          );
        }

        return (
          <WebhookSubscriptionForm
            mode="edit"
            company_id={company_id}
            subscription={subscription}
            onSave={onSave}
            onDelete={onDelete}
          />
        );
      }}
    </WebhookSubscriptionLoadingContainer>
  );
}

export function WebhookSubscriptionContainer() {
  const { company_id, webhook_subscription_id } = useParams() as {
    company_id: string;
    webhook_subscription_id?: string;
  };

  if (isNil(webhook_subscription_id)) {
    return <CreateModeContainer company_id={company_id} />;
  }

  return <EditModeContainer company_id={company_id} webhook_subscription_id={webhook_subscription_id} />;
}
